import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import ErrorHandler from 'common/error/components/error-handler';
import UserManagementTable from './user-management-table';
import UserManagementToolbar from './user-management-toolbar';
import AddUsersModal from '../../containers/user-management/add-user-modal-container';

const TooltipTitle = styled.div`
  font-weight: bold;
`;

const PermissionList = styled.div`
  width: 500px;
`;

const UserManagement = ({
  menuVisible,
  updateSort,
  activeSort,
  updateSearchString,
  activeSearchString,
  activeServiceCenter,
  fetchUsers,
  tableData,
  isDataLoading,
  roles,
  updateUser,
  deleteUser,
  fetchUsersNext,
  addUser,
  deleteSuccess,
  isDeleting,
  isUpdating,
  error,
  permissions,
  fetchPermissions,
}) => {
  const [addUsersVisible, setAddUsersVisible] = useState(false);

  const fetchData = (sort, searchString) => {
    // eslint-disable-next-line
    fetchUsers({ sort, searchString, page: 1 })
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  useEffect(() => {
    fetchData(activeSort, activeSearchString);
  }, [activeSearchString, activeSort, activeServiceCenter]);

  useEffect(() => {
    if (deleteSuccess && !addUsersVisible) {
      fetchData(activeSort, activeSearchString);
    }
  }, [deleteSuccess]);

  const columns =
    tableData?.columnDefinitions &&
    tableData.columnDefinitions?.map((c) => {
      if (!c.displayName) {
        return {
          renderLabelFn: (value, record) => record && record[c.displayField],
          ...c,
        };
      }
      return {
        label: c.displayName,
        ...c,
      };
    });

  const onAddUsersClose = () => {
    fetchData(activeSort, activeSearchString);
    setAddUsersVisible(false);
  };

  const handleUpdate = (user, role) => {
    updateUser({ userId: user, roleId: role });
  };

  const handleDelete = (user, shouldUpdate) => {
    deleteUser({
      userId: user,
      shouldUpdateLatestAddedUsers: shouldUpdate,
    });
  };

  const handleAdd = (user) => {
    addUser({ userId: user });
  };

  const getTooltipText = () =>
    permissions?.map(p =>
      <div key={p.roleId}>
        <TooltipTitle>{p.roleDescription}</TooltipTitle>
        <PermissionList>
          {p.permissions?.map(pp =>
            pp.permissionDescription,
          ).join(' \u{1F784} ')}
        </PermissionList>
        <br />
      </div>,
    );

  return (
    <>
      <ErrorHandler type='notification' error={error} />
      <UserManagementToolbar
        menuVisible={menuVisible}
        data={tableData.data}
        columnDef={columns}
        activeSort={activeSort}
        updateSort={updateSort}
        addUsers={() => setAddUsersVisible(true)}
        updateSearchString={updateSearchString}
        activeSearchString={activeSearchString}
      />
      <UserManagementTable
        disabled={isDeleting || isUpdating}
        tableData={tableData}
        isLoading={isDataLoading}
        updateUser={handleUpdate}
        fetchMore={fetchUsersNext}
        roles={roles}
        deleteUser={handleDelete}
        tooltipText={getTooltipText()}
      />
      <AddUsersModal
        visible={addUsersVisible}
        onClose={onAddUsersClose}
        handleAdd={handleAdd}
        handleUpdate={handleUpdate}
        handleDelete={handleDelete}
        tooltipText={getTooltipText()}
      />
    </>
  );
};

UserManagement.propTypes = {
  menuVisible: PropTypes.bool,
  updateSort: PropTypes.func,
  activeSort: PropTypes.string,
  updateSearchString: PropTypes.func,
  activeSearchString: PropTypes.string,
  activeServiceCenter: PropTypes.string,
  isDataLoading: PropTypes.bool,
  tableData: PropTypes.object,
  fetchUsers: PropTypes.func,
  roles: PropTypes.array,
  updateUser: PropTypes.func,
  deleteUser: PropTypes.func,
  fetchUsersNext: PropTypes.func,
  addUser: PropTypes.func,
  deleteSuccess: PropTypes.bool,
  isDeleting: PropTypes.bool,
  isUpdating: PropTypes.bool,
  error: PropTypes.object,
  permissions: PropTypes.array,
  fetchPermissions: PropTypes.func,
};

export default UserManagement;
